import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Stack,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { repAPI } from '../../services/api';

const RepPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Create/Edit Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    promoType: 'PERCENTAGE_DISCOUNT',
    percentDiscount: 0,
    fixedPricePerUnit: 0,
    buyQuantity: 1,
    getQuantity: 1,
    getDiscountPercent: 0,
    applicableClientIds: [],
    usageLimitPerCustomer: null,
    validFrom: '',
    validUntil: '',
    internalNotes: ''
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchClients();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getMyPromoCodes();
      if (response.success) {
        setPromoCodes(response.data || []);
      }
    } catch (err) {
      setError('Failed to load promo codes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await repAPI.getMyClients();
      if (response.success) {
        setClients(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const handleOpenCreate = () => {
    setEditingPromo(null);
    setFormData({
      code: '',
      description: '',
      promoType: 'PERCENTAGE_DISCOUNT',
      percentDiscount: 0,
      fixedPricePerUnit: 0,
      buyQuantity: 1,
      getQuantity: 1,
      getDiscountPercent: 0,
      applicableClientIds: [],
      usageLimitPerCustomer: null,
      validFrom: '',
      validUntil: '',
      internalNotes: ''
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code || '',
      description: promo.description || '',
      promoType: promo.promoType || 'PERCENTAGE_DISCOUNT',
      percentDiscount: promo.percentDiscount ? parseFloat(promo.percentDiscount) : 0,
      fixedPricePerUnit: promo.fixedPricePerUnit ? parseFloat(promo.fixedPricePerUnit) : 0,
      buyQuantity: promo.buyQuantity || 1,
      getQuantity: promo.getQuantity || 1,
      getDiscountPercent: promo.getDiscountPercent ? parseFloat(promo.getDiscountPercent) : 0,
      applicableClientIds: promo.applicableClients ? JSON.parse(promo.applicableClients) : [],
      usageLimitPerCustomer: promo.usageLimitPerCustomer || null,
      validFrom: promo.validFrom ? new Date(promo.validFrom).toISOString().split('T')[0] : '',
      validUntil: promo.validUntil ? new Date(promo.validUntil).toISOString().split('T')[0] : '',
      internalNotes: promo.internalNotes || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.code.trim()) {
        setError('Promo code is required');
        return;
      }

      const data = {
        ...formData,
        code: formData.code.toUpperCase().trim()
      };

      let response;
      if (editingPromo) {
        response = await repAPI.updatePromoCode(editingPromo.id, data);
      } else {
        response = await repAPI.createPromoCode(data);
      }

      if (response.success) {
        setSuccess(editingPromo ? 'Promo code updated!' : 'Promo code created!');
        setDialogOpen(false);
        fetchPromoCodes();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to save promo code');
    }
  };

  const getStatusColor = (status) => {
    return status === 'ACTIVE' ? 'success' : 'default';
  };

  const getPromoTypeLabel = (type) => {
    const labels = {
      PERCENTAGE_DISCOUNT: 'Percentage Discount',
      FIXED_PRICE: 'Fixed Price',
      BUY_X_GET_Y_FREE: 'Buy X Get Y Free',
      BUY_X_GET_Y_DISCOUNTED: 'Buy X Get Y Discounted'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Promo Code Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            bgcolor: '#039582',
            '&:hover': { bgcolor: '#027a6b' },
            textTransform: 'none'
          }}
        >
          Create Promo Code
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Applicable To</TableCell>
              <TableCell>Valid From</TableCell>
              <TableCell>Valid Until</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promoCodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No promo codes created yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              promoCodes.map((promo) => {
                const applicableClients = promo.applicableClients 
                  ? (typeof promo.applicableClients === 'string' 
                      ? JSON.parse(promo.applicableClients) 
                      : promo.applicableClients)
                  : [];
                
                return (
                  <TableRow key={promo.id} hover>
                    <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {promo.code}
                    </TableCell>
                    <TableCell>{getPromoTypeLabel(promo.promoType)}</TableCell>
                    <TableCell>
                      {promo.promoType === 'PERCENTAGE_DISCOUNT' 
                        ? `${promo.percentDiscount}%`
                        : promo.promoType === 'FIXED_PRICE'
                        ? `$${promo.fixedPricePerUnit}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {applicableClients.length > 0 
                        ? `${applicableClients.length} client(s)`
                        : 'All clients'}
                    </TableCell>
                    <TableCell>
                      {promo.validFrom 
                        ? new Date(promo.validFrom).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {promo.validUntil 
                        ? new Date(promo.validUntil).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={promo.status || 'ACTIVE'} 
                        color={getStatusColor(promo.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(promo)}
                        sx={{ color: '#039582' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#039582', color: 'white' }}>
          {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Promo Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
              helperText="Code will be converted to uppercase"
              disabled={!!editingPromo}
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>Promo Type</InputLabel>
              <Select
                value={formData.promoType}
                onChange={(e) => setFormData({ ...formData, promoType: e.target.value })}
                label="Promo Type"
              >
                <MenuItem value="PERCENTAGE_DISCOUNT">Percentage Discount</MenuItem>
                <MenuItem value="FIXED_PRICE">Fixed Price</MenuItem>
                <MenuItem value="BUY_X_GET_Y_FREE">Buy X Get Y Free</MenuItem>
                <MenuItem value="BUY_X_GET_Y_DISCOUNTED">Buy X Get Y Discounted</MenuItem>
              </Select>
            </FormControl>

            {formData.promoType === 'PERCENTAGE_DISCOUNT' && (
              <TextField
                fullWidth
                type="number"
                label="Discount Percentage"
                value={formData.percentDiscount}
                onChange={(e) => setFormData({ ...formData, percentDiscount: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 100 }}
                helperText="Enter discount percentage (0-100)"
              />
            )}

            {formData.promoType === 'FIXED_PRICE' && (
              <TextField
                fullWidth
                type="number"
                label="Fixed Price Per Unit"
                value={formData.fixedPricePerUnit}
                onChange={(e) => setFormData({ ...formData, fixedPricePerUnit: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            )}

            {(formData.promoType === 'BUY_X_GET_Y_FREE' || formData.promoType === 'BUY_X_GET_Y_DISCOUNTED') && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Buy Quantity"
                      value={formData.buyQuantity}
                      onChange={(e) => setFormData({ ...formData, buyQuantity: parseInt(e.target.value) || 1 })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Get Quantity"
                      value={formData.getQuantity}
                      onChange={(e) => setFormData({ ...formData, getQuantity: parseInt(e.target.value) || 1 })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                </Grid>
                {formData.promoType === 'BUY_X_GET_Y_DISCOUNTED' && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Discount on Get Items (%)"
                    value={formData.getDiscountPercent}
                    onChange={(e) => setFormData({ ...formData, getDiscountPercent: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0, max: 100 }}
                  />
                )}
              </>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Applicable Clients
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.applicableClientIds.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, applicableClientIds: [] });
                      }
                    }}
                  />
                }
                label="Apply to all my clients"
              />
              {!formData.applicableClientIds.length && (
                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                  {clients.map((client) => (
                    <FormControlLabel
                      key={client.id}
                      control={
                        <Checkbox
                          checked={formData.applicableClientIds.includes(client.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                applicableClientIds: [...formData.applicableClientIds, client.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                applicableClientIds: formData.applicableClientIds.filter(id => id !== client.id)
                              });
                            }
                          }}
                        />
                      }
                      label={client.profile?.clinicName || `${client.firstName} ${client.lastName}` || client.email}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              type="number"
              label="Usage Limit Per Customer (optional)"
              value={formData.usageLimitPerCustomer || ''}
              onChange={(e) => setFormData({ ...formData, usageLimitPerCustomer: e.target.value ? parseInt(e.target.value) : null })}
              inputProps={{ min: 1 }}
              helperText="Leave empty for unlimited"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Valid From"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Valid Until"
                  value={formData.validUntil}
                  onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Internal Notes"
              value={formData.internalNotes}
              onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
              placeholder="Private notes about this promo code..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              bgcolor: '#039582',
              '&:hover': { bgcolor: '#027a6b' },
              textTransform: 'none'
            }}
          >
            {editingPromo ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepPromoCodes;

